import {
  useContactDetailsData,
  useContactDetailsState,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/context';
import { CreditUsageType } from '@aibees/shared/definition';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { memo, useCallback, useMemo, useRef } from 'react';
import { CreditRuleCoin } from '../credit-rule-coin';
import * as Styled from './styles';
import * as SX from './sx';
import type { UnlockProfileDetailsProps as Props } from './types';

export const UnlockProfileDetails = memo(({ className }: Props) => {
  const hover = useBoolean(false);

  const { loading, failed, unlocked } = useContactDetailsState();
  const {
    handleEnrichContactDetails,
    hasUpdates,
    unlockableItems,
    hasJobChange,
  } = useContactDetailsData();

  const handleUnlock = useCallback(() => {
    handleEnrichContactDetails({});
    hover.onFalse();
  }, [handleEnrichContactDetails, hover]);

  const handleUpdate = useCallback(() => {
    handleEnrichContactDetails({
      updateMode: true,
      updateJobChangeMode: hasJobChange,
    });
    hover.onFalse();
  }, [handleEnrichContactDetails, hasJobChange, hover]);

  const action = useMemo(() => {
    if (failed) return { label: 'Retry', handler: handleUnlock };
    if (hasUpdates) return { label: 'Update', handler: handleUpdate };
    if (!unlocked && !!unlockableItems.length)
      return { label: 'Unlock All', handler: handleUnlock };
    return null;
  }, [
    failed,
    hasUpdates,
    unlocked,
    unlockableItems.length,
    handleUnlock,
    handleUpdate,
  ]);

  const handleClick = () => {
    action?.handler();
  };

  // Preserve last action's label during loading so the button keeps its
  // natural width (MUI hides the text via `color: transparent` but the width
  // is still occupied) and we avoid a layout jump on the way out.
  const lastActionRef = useRef(action);
  if (action) lastActionRef.current = action;
  const displayLabel = action?.label ?? lastActionRef.current?.label;

  return (
    <Styled.Wrapper
      className={classnames('UnlockProfileDetails', className)}
      sx={SX.root}
    >
      {hover.value && !loading && (
        <CreditRuleCoin
          type={
            hasUpdates
              ? CreditUsageType.CONTACT_PROFILE_UPDATE
              : CreditUsageType.CONTACT_PROFILE_REVEAL
          }
          sx={SX.creditTag}
        />
      )}
      {(action || loading) && (
        <LoadingButton
          variant="contained"
          color="primary"
          size="xsmall"
          loading={loading}
          onClick={handleClick}
          onMouseEnter={hover.onTrue}
          onMouseLeave={hover.onFalse}
        >
          {displayLabel}
        </LoadingButton>
      )}
    </Styled.Wrapper>
  );
});

UnlockProfileDetails.displayName = 'UnlockProfileDetails';
